import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { createGroup, hideGroup, publishGroup, validateGroupPayload as validateGroupPayloadLocal, toErrorMessage } from "@/services/groups/groupMutations";
import type { ValidationResult as GroupValidationResult } from "@/services/groups/groupMutations";
export interface SubmitArgs {
  readonly payload: ApiCreateGroup;
  readonly previousGroup?: ApiGroupFull | null | undefined;
  readonly currentHandle?: string | null | undefined;
  readonly publish?: boolean | undefined;
}
type SubmitResult = {
  readonly ok: true;
  readonly group: ApiGroupFull;
  readonly published: boolean;
} | {
  readonly ok: false;
  readonly reason: "validation" | "auth" | "api" | "busy";
  readonly error: string;
  readonly validation?: GroupValidationResult | undefined;
};
interface TestArgs {
  readonly payload: ApiCreateGroup;
  readonly nameFallback: string;
}
type TestResult = {
  readonly ok: true;
  readonly group: ApiGroupFull;
} | {
  readonly ok: false;
  readonly reason: "validation" | "auth" | "api" | "busy";
  readonly error: string;
  readonly validation?: GroupValidationResult | undefined;
};
interface UseGroupMutationsArgs {
  readonly requestAuth: () => Promise<{
    success: boolean;
  }>;
  readonly onGroupCreate?: (() => void) | undefined;
}
interface UpdateVisibilityArgs {
  readonly groupId: string;
  readonly visible: boolean;
  readonly oldVersionId?: string | null | undefined;
  readonly skipAuth?: boolean | undefined;
}
type UpdateVisibilityResult = {
  readonly ok: true;
  readonly groupId: string;
  readonly visible: boolean;
  readonly group?: ApiGroupFull | undefined;
} | {
  readonly ok: false;
  readonly reason: "auth" | "api" | "busy";
  readonly error: string;
};
const resolveOldVersionId = ({
  previousGroup,
  currentHandle
}: {
  readonly previousGroup?: ApiGroupFull | null | undefined;
  readonly currentHandle?: string | null | undefined;
}): string | null => {
  if (!previousGroup?.id) {
    return null;
  }
  const previousHandle = previousGroup.created_by?.handle;
  if (!previousHandle || !currentHandle) {
    return null;
  }
  return previousHandle.toLowerCase() === currentHandle.toLowerCase() ? previousGroup.id : null;
};
export const useGroupMutations = ({
  requestAuth,
  onGroupCreate
}: UseGroupMutationsArgs) => {
  const createGroupMutation = useMutation({
    mutationFn: async ({
      payload,
      nameOverride
    }: {
      readonly payload: ApiCreateGroup;
      readonly nameOverride?: string | undefined;
    }) => await createGroup({
      payload,
      nameOverride
    })
  });
  const publishGroupMutation = useMutation({
    mutationFn: async ({
      id,
      oldVersionId
    }: {
      readonly id: string;
      readonly oldVersionId: string | null;
    }) => await publishGroup({
      id,
      oldVersionId
    })
  });
  const hideGroupMutation = useMutation({
    mutationFn: async (groupId: string) => await hideGroup({
      id: groupId
    })
  });
  const testGroupMutation = useMutation({
    mutationFn: async ({
      payload: payload_0,
      nameOverride: nameOverride_0
    }: {
      readonly payload: ApiCreateGroup;
      readonly nameOverride?: string | undefined;
    }) => await createGroup({
      payload: payload_0,
      nameOverride: nameOverride_0
    })
  });
  const updateVisibility = useCallback(async ({
    groupId: groupId_0,
    visible,
    oldVersionId: oldVersionId_0 = null,
    skipAuth = false
  }: UpdateVisibilityArgs): Promise<UpdateVisibilityResult> => {
    const isPublishing = visible;
    const isPending = isPublishing ? publishGroupMutation.isPending : hideGroupMutation.isPending;
    if (isPending) {
      return {
        ok: false,
        reason: "busy",
        error: "Another group action is already running."
      };
    }
    if (!skipAuth) {
      const authResult = await requestAuth();
      if (!authResult.success) {
        return {
          ok: false,
          reason: "auth",
          error: "Authentication was cancelled."
        };
      }
    }
    let updatedGroup: ApiGroupFull | undefined;
    try {
      if (visible) {
        updatedGroup = await publishGroupMutation.mutateAsync({
          id: groupId_0,
          oldVersionId: oldVersionId_0
        });
        onGroupCreate?.();
      } else {
        await hideGroupMutation.mutateAsync(groupId_0);
      }
    } catch (error) {
      return {
        ok: false,
        reason: "api",
        error: toErrorMessage(error)
      };
    }
    return {
      ok: true,
      groupId: groupId_0,
      visible,
      ...(updatedGroup ? {
        group: updatedGroup
      } : {})
    };
  }, [hideGroupMutation, onGroupCreate, publishGroupMutation, requestAuth]);
  const submit = useCallback(async ({
    payload: payload_1,
    previousGroup = null,
    currentHandle = null,
    publish = true
  }: SubmitArgs): Promise<SubmitResult> => {
    if (createGroupMutation.isPending || publishGroupMutation.isPending) {
      return {
        ok: false,
        reason: "busy",
        error: "Another group action is already running."
      };
    }
    const trimmedName = payload_1.name.trim();
    if (!trimmedName.length) {
      return {
        ok: false,
        reason: "validation",
        error: "Please name your group."
      };
    }
    const validation = validateGroupPayloadLocal(payload_1);
    if (!validation.valid) {
      return {
        ok: false,
        reason: "validation",
        error: "Group configuration is invalid.",
        validation
      };
    }
    const authResult_0 = await requestAuth();
    if (!authResult_0.success) {
      return {
        ok: false,
        reason: "auth",
        error: "Authentication was cancelled."
      };
    }
    let created: ApiGroupFull;
    try {
      created = await createGroupMutation.mutateAsync({
        payload: payload_1,
        nameOverride: trimmedName
      });
    } catch (error_0) {
      return {
        ok: false,
        reason: "api",
        error: toErrorMessage(error_0)
      };
    }
    if (!publish) {
      return {
        ok: true,
        group: created,
        published: false
      };
    }
    const oldVersionId_1 = resolveOldVersionId({
      previousGroup,
      currentHandle
    });
    const visibilityResult = await updateVisibility({
      groupId: created.id,
      visible: true,
      oldVersionId: oldVersionId_1,
      skipAuth: true
    });
    if (!visibilityResult.ok) {
      return {
        ok: false,
        reason: visibilityResult.reason,
        error: visibilityResult.error
      };
    }
    return {
      ok: true,
      group: visibilityResult.group ?? created,
      published: true
    };
  }, [createGroupMutation, publishGroupMutation, requestAuth, updateVisibility]);
  const runTest = useCallback(async ({
    payload: payload_2,
    nameFallback
  }: TestArgs): Promise<TestResult> => {
    if (testGroupMutation.isPending) {
      return {
        ok: false,
        reason: "busy",
        error: "Another group test is already running."
      };
    }
    const trimmedName_0 = payload_2.name.trim();
    const effectiveName = trimmedName_0.length ? trimmedName_0 : nameFallback;
    const validation_0 = validateGroupPayloadLocal(payload_2);
    if (!validation_0.valid) {
      return {
        ok: false,
        reason: "validation",
        error: "Group configuration is invalid.",
        validation: validation_0
      };
    }
    const authResult_1 = await requestAuth();
    if (!authResult_1.success) {
      return {
        ok: false,
        reason: "auth",
        error: "Authentication was cancelled."
      };
    }
    try {
      const created_0 = await testGroupMutation.mutateAsync({
        payload: payload_2,
        nameOverride: effectiveName
      });
      return {
        ok: true,
        group: created_0
      };
    } catch (error_1) {
      return {
        ok: false,
        reason: "api",
        error: toErrorMessage(error_1)
      };
    }
  }, [requestAuth, testGroupMutation]);
  return {
    validate: validateGroupPayloadLocal,
    submit,
    runTest,
    updateVisibility,
    isSubmitting: createGroupMutation.isPending || publishGroupMutation.isPending,
    isTesting: testGroupMutation.isPending,
    isUpdatingVisibility: publishGroupMutation.isPending || hideGroupMutation.isPending
  };
};